import { useEffect, useMemo, useState } from "react";
import { usePanelActions } from "./panel_engine/PanelActionsContext";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";

import {
  activateLoyaltyProgram,
  archiveLoyaltyProgram,
  cancelLoyaltyBenefit,
  createLoyaltyProgram,
  deactivateLoyaltyProgram,
  fetchLoyaltyAccounts,
  fetchLoyaltyBenefits,
  fetchLoyaltyPrograms,
  fetchLoyaltyTransactions,
  updateLoyaltyProgram,
} from "../../services/adminApi";

import AdminButton from "./action_engine/AdminButton";
const DEFAULT_FORM = {
  name: "",
  status: "active",
  trigger_type: "purchase_amount",
  threshold_amount: "100",
  threshold_count: "0",
  benefit_type: "percent_discount",
  benefit_value: "10",
  is_stackable: false,
  validity_days: "365",
};

export default function LoyaltyAdminPage({ embedded = false }) {
  const [programs, setPrograms] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [editingProgramUuid, setEditingProgramUuid] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const activeProgramsCount = useMemo(
    () => programs.filter((program) => program.status === "active").length,
    [programs]
  );

  const availableBenefitsCount = useMemo(
    () => benefits.filter((benefit) => benefit.status === "available").length,
    [benefits]
  );

  const loadData = async () => {
    setLoading(true);
    setError("");

    try {
      const [
        programsResponse,
        accountsResponse,
        benefitsResponse,
        transactionsResponse,
      ] = await Promise.all([
        fetchLoyaltyPrograms(),
        fetchLoyaltyAccounts(),
        fetchLoyaltyBenefits(),
        fetchLoyaltyTransactions(),
      ]);

      setPrograms(programsResponse.data?.programs || []);
      setAccounts(accountsResponse.data?.accounts || []);
      setBenefits(benefitsResponse.data?.benefits || []);
      setTransactions(transactionsResponse.data?.transactions || []);
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Impossible de charger l'administration fidélité."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submitProgram = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");

    try {
      const payload = {
        ...form,
        threshold_amount: Number(form.threshold_amount || 0),
        threshold_count: Number(form.threshold_count || 0),
        benefit_value: Number(form.benefit_value || 0),
        validity_days: form.validity_days ? Number(form.validity_days) : null,
      };

      if (editingProgramUuid) {
        await updateLoyaltyProgram(editingProgramUuid, payload);
        setMessage("Programme fidélité modifié.");
      } else {
        await createLoyaltyProgram(payload);
        setMessage("Programme fidélité créé.");
      }

      setForm(DEFAULT_FORM);
      setEditingProgramUuid(null);
      await loadData();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Impossible d'enregistrer le programme fidélité."
      );
    } finally {
      setSaving(false);
    }
  };


  const editProgram = (program) => {
    setEditingProgramUuid(program.uuid);
    setForm({
      name: program.name || "",
      status: program.status || "active",
      trigger_type: program.trigger_type || "purchase_amount",
      threshold_amount: String(program.threshold_amount ?? "0"),
      threshold_count: String(program.threshold_count ?? "0"),
      benefit_type: program.benefit_type || "percent_discount",
      benefit_value: String(program.benefit_value ?? "0"),
      is_stackable: Boolean(program.is_stackable),
      validity_days:
        program.validity_days === null || program.validity_days === undefined
          ? ""
          : String(program.validity_days),
    });
    setMessage("");
    setError("");
  };

  const resetProgramForm = () => {
    setEditingProgramUuid(null);
    setForm(DEFAULT_FORM);
    setMessage("");
    setError("");
  };

  const changeProgramStatus = async (programUuid, action) => {
    if (!programUuid) return;

    setError("");
    setMessage("");

    try {
      if (action === "activate") {
        await activateLoyaltyProgram(programUuid);
        setMessage("Programme activé.");
      } else if (action === "deactivate") {
        await deactivateLoyaltyProgram(programUuid);
        setMessage("Programme désactivé.");
      } else if (action === "archive") {
        await archiveLoyaltyProgram(programUuid);
        setMessage("Programme archivé.");
      }

      await loadData();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Impossible de modifier le statut du programme."
      );
    }
  };

  const cancelBenefit = async (benefitUuid) => {
    if (!benefitUuid) return;

    setError("");
    setMessage("");

    try {
      await cancelLoyaltyBenefit(benefitUuid);
      setMessage("Récompense annulée.");
      await loadData();
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Impossible d'annuler la récompense."
      );
    }
  };

  usePanelActions(
    embedded
      ? [
          {
            key: "refresh-loyalty",
            icon: "↻",
            label: loading ? "Rafraîchissement en cours" : "Rafraîchir",
            disabled: loading,
            onClick: loadData,
          },
        ]
      : []
  );

  return (
    <PageWrapper $embedded={embedded}>
      {!embedded && (
        <Header>
          <div>
            <Eyebrow>Administration</Eyebrow>
            <h1>Fidélité</h1>
            <p>
              Pilotez les programmes, comptes, récompenses et transactions du
              Loyalty Engine.
            </p>
          </div>

          <div style={{ display: "flex", gap: "0.75rem" }}>
            <AdminButton variant="secondary"
              type="button"
              onClick={() => navigate("/admin/dashboard")}
            >
              ← Retour au tableau de bord
            </AdminButton>

            <AdminButton variant="primary" loading={loading}
              type="button"
              onClick={loadData}
              disabled={loading}
            >
              {loading ? "Chargement..." : "Rafraîchir"}
            </AdminButton>
          </div>
        </Header>
      )}

      {message && <PanelMessage>{message}</PanelMessage>}
      {error && <PanelMessage $error>{error}</PanelMessage>}

      <StatsGrid>
        <StatCard>
          <strong>{programs.length}</strong>
          <span>programmes</span>
        </StatCard>
        <StatCard>
          <strong>{activeProgramsCount}</strong>
          <span>actifs</span>
        </StatCard>
        <StatCard>
          <strong>{accounts.length}</strong>
          <span>comptes</span>
        </StatCard>
        <StatCard>
          <strong>{availableBenefitsCount}</strong>
          <span>récompenses disponibles</span>
        </StatCard>
      </StatsGrid>

      <Grid>
        <Card>
          <h2>{editingProgramUuid ? "Modifier un programme" : "Créer un programme"}</h2>

          <Form onSubmit={submitProgram}>
            <Field>
              <span>Nom</span>
              <input
                value={form.name}
                onChange={(event) => updateField("name", event.target.value)}
                placeholder="10% tous les 100€"
                required
              />
            </Field>

            <Field>
              <span>Statut</span>
              <select
                value={form.status}
                onChange={(event) => updateField("status", event.target.value)}
              >
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
                <option value="archived">Archivé</option>
              </select>
            </Field>

            <Field>
              <span>Seuil d'achat (€)</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.threshold_amount}
                onChange={(event) =>
                  updateField("threshold_amount", event.target.value)
                }
              />
            </Field>

            <Field>
              <span>Réduction (%)</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.benefit_value}
                onChange={(event) =>
                  updateField("benefit_value", event.target.value)
                }
              />
            </Field>

            <Field>
              <span>Validité jours</span>
              <input
                type="number"
                min="0"
                value={form.validity_days}
                onChange={(event) =>
                  updateField("validity_days", event.target.value)
                }
              />
            </Field>

            <CheckboxLabel>
              <input
                type="checkbox"
                checked={form.is_stackable}
                onChange={(event) =>
                  updateField("is_stackable", event.target.checked)
                }
              />
              <span>Cumulable</span>
            </CheckboxLabel>

            <ButtonRow>
              <AdminButton variant="primary" type="submit" disabled={saving}>
                {saving
                  ? "Enregistrement..."
                  : editingProgramUuid
                    ? "Modifier le programme"
                    : "Créer le programme"}
              </AdminButton>

              {editingProgramUuid && (
                <AdminButton variant="secondary" type="button" onClick={resetProgramForm}>
                  Annuler l'édition
                </AdminButton>
              )}
            </ButtonRow>
          </Form>
        </Card>

        <Card>
          <h2>Programmes</h2>
          <Table>
            <thead>
              <tr>
                <th>Nom</th>
                <th>Statut</th>
                <th>Seuil</th>
                <th>Avantage</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {programs.map((program) => (
                <tr key={program.uuid}>
                  <td>{program.name}</td>
                  <td>{program.status}</td>
                  <td>{formatAmount(program.threshold_amount)}</td>
                  <td>
                    {program.benefit_value}
                    {program.benefit_type === "percent_discount" ? "%" : ""}
                  </td>
                  <td>
                    <ActionGroup>
                      <AdminButton size="small" type="button" onClick={() => editProgram(program)}>
                        Éditer
                      </AdminButton>
                      {program.status !== "active" && (
                        <AdminButton size="small"
                          type="button"
                          onClick={() => changeProgramStatus(program.uuid, "activate")}
                        >
                          Activer
                        </AdminButton>
                      )}
                      {program.status === "active" && (
                        <AdminButton size="small"
                          type="button"
                          onClick={() => changeProgramStatus(program.uuid, "deactivate")}
                        >
                          Désactiver
                        </AdminButton>
                      )}
                      {program.status !== "archived" && (
                        <AdminButton variant="danger" size="small"
                          type="button"
                          onClick={() => changeProgramStatus(program.uuid, "archive")}
                        >
                          Archiver
                        </AdminButton>
                      )}
                    </ActionGroup>
                  </td>
                </tr>
              ))}
              {programs.length === 0 && (
                <tr>
                  <td colSpan="5">Aucun programme fidélité.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card>

        <Card>
          <h2>Comptes fidélité</h2>
          <Table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Cycle</th>
                <th>Gagnées</th>
                <th>Utilisées</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((account) => (
                <tr key={account.uuid}>
                  <td>{shortUuid(account.client_uuid)}</td>
                  <td>{formatAmount(account.current_cycle_amount)}</td>
                  <td>{account.earned_benefits_count}</td>
                  <td>{account.redeemed_benefits_count}</td>
                </tr>
              ))}
              {accounts.length === 0 && (
                <tr>
                  <td colSpan="4">Aucun compte fidélité.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card>

        <Card>
          <h2>Récompenses</h2>
          <Table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Statut</th>
                <th>Valeur</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {benefits.map((benefit) => (
                <tr key={benefit.uuid}>
                  <td>{shortUuid(benefit.client_uuid)}</td>
                  <td>{benefit.status}</td>
                  <td>
                    {benefit.benefit_value}
                    {benefit.benefit_type === "percent_discount" ? "%" : ""}
                  </td>
                  <td>
                    {benefit.status === "available" ? (
                      <AdminButton size="small"
                        type="button"
                        onClick={() => cancelBenefit(benefit.uuid)}
                      >
                        Annuler
                      </AdminButton>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))}
              {benefits.length === 0 && (
                <tr>
                  <td colSpan="4">Aucune récompense.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card>

        <Card $full>
          <h2>Transactions</h2>
          <Table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Type</th>
                <th>Montant</th>
                <th>Ticket</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction) => (
                <tr key={transaction.uuid}>
                  <td>{shortUuid(transaction.client_uuid)}</td>
                  <td>{transaction.transaction_type}</td>
                  <td>{formatAmount(transaction.amount)}</td>
                  <td>{shortUuid(transaction.receipt_uuid)}</td>
                </tr>
              ))}
              {transactions.length === 0 && (
                <tr>
                  <td colSpan="4">Aucune transaction fidélité.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card>
      </Grid>
    </PageWrapper>
  );
}

function formatAmount(value) {
  return `${Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

function shortUuid(value) {
  if (!value) return "-";
  return String(value).slice(0, 8);
}

const PageWrapper = styled.main`
  min-height: ${({ $embedded }) => ($embedded ? "auto" : "100%")};
  padding: ${({ $embedded }) => ($embedded ? "0" : "1.5rem")};
  color: #f7f7f7;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1rem;

  h1 {
    margin: 0.25rem 0;
    font-size: clamp(1.8rem, 4vw, 2.4rem);
    font-weight: 950;
  }

  p {
    margin: 0;
    opacity: 0.72;
    max-width: 720px;
  }

  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

const Eyebrow = styled.div`
  display: inline-block;
  padding: 0.32rem 0.68rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.75rem;
  font-weight: 950;
`;

const StatsGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
  margin-bottom: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.article`
  padding: 1rem;
  border-radius: 18px;
  background: #1d263d;
  border: 1px solid rgba(255, 255, 255, 0.08);

  strong {
    display: block;
    font-size: 1.8rem;
    font-weight: 950;
  }

  span {
    opacity: 0.72;
  }
`;

const Grid = styled.section`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.article`
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};
  padding: 1rem;
  border-radius: 22px;
  background: #1d263d;
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow-x: auto;

  h2 {
    margin: 0 0 1rem;
    font-size: 1.1rem;
    font-weight: 950;
  }
`;

const Form = styled.form`
  display: grid;
  gap: 0.8rem;
`;

const Field = styled.label`
  display: grid;
  gap: 0.35rem;

  span {
    font-size: 0.78rem;
    opacity: 0.74;
    font-weight: 850;
  }

  input,
  select {
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 12px;
    padding: 0.78rem 0.85rem;
    background: #202a43;
    color: #f7f7f7;
    box-sizing: border-box;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.65rem;
  flex-wrap: wrap;
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 0.45rem;
  flex-wrap: wrap;
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  font-weight: 850;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 620px;

  th,
  td {
    text-align: left;
    padding: 0.72rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    vertical-align: top;
  }

  th {
    font-size: 0.76rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    opacity: 0.68;
  }

  td {
    font-size: 0.9rem;
  }
`;






const PanelMessage = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;
